// "Are we connected, and to what": the state of the WebSocket to control and of
// every site's agent. Feeds the status strip; everything else may read it too.

import { defineStore } from 'pinia'
import { computed, ref, shallowRef } from 'vue'
import type { AgentStatus, ID, Site, SiteRole } from '@/api/types'
import type { WsState } from '@/ws/client'

export interface AgentSummary {
  /** null: control has not told us yet. */
  online: boolean | null
  /** Local receive time of the last status message, epoch ms. */
  lastStatusAt: number | null
  version: string | null
  effectiveRole: SiteRole | null
  clockSynced: boolean | null
  channels: number
  /** Status messages older than this are not trusted. */
  stale: boolean
}

/** A 1 Hz status stream that has been silent for this long is considered dead. */
export const STATUS_STALE_MS = 5000

const EMPTY: Omit<AgentSummary, 'stale'> = {
  online: null,
  lastStatusAt: null,
  version: null,
  effectiveRole: null,
  clockSynced: null,
  channels: 0,
}

export const useLinkStore = defineStore('link', () => {
  const wsState = ref<WsState>('idle')
  const retryDelayMs = ref<number | null>(null)
  const sites = shallowRef<Site[]>([])
  const agents = shallowRef<ReadonlyMap<ID, Omit<AgentSummary, 'stale'>>>(new Map())
  /** Last full status per site; replaced once a second, never mutated. */
  const statuses = shallowRef<ReadonlyMap<ID, AgentStatus>>(new Map())
  /** Ticks once a second so staleness is re-evaluated without new messages. */
  const clock = ref(Date.now())

  const connected = computed(() => wsState.value === 'open')

  function setWsState(state: WsState, delay: number | null): void {
    wsState.value = state
    retryDelayMs.value = delay
    if (state !== 'open') {
      // Without a socket we know nothing about agents; do not show yesterday's green.
      const next = new Map<ID, Omit<AgentSummary, 'stale'>>()
      for (const [id, a] of agents.value) next.set(id, { ...a, online: null })
      agents.value = next
    }
  }

  function setSites(list: readonly Site[]): void {
    sites.value = [...list]
  }

  function patchAgent(siteId: ID, patch: Partial<Omit<AgentSummary, 'stale'>>): void {
    const next = new Map(agents.value)
    next.set(siteId, { ...(next.get(siteId) ?? EMPTY), ...patch })
    agents.value = next
  }

  function applyAgentOnline(siteId: ID, online: boolean): void {
    patchAgent(siteId, { online })
  }

  function applyStatus(status: AgentStatus, receivedAt = Date.now()): void {
    const next = new Map(statuses.value)
    next.set(status.site_id, status)
    statuses.value = next
    patchAgent(status.site_id, {
      // A status message is itself proof that the agent is connected.
      online: true,
      lastStatusAt: receivedAt,
      version: status.version,
      effectiveRole: status.effective_role,
      clockSynced: status.clock_synced,
      channels: status.channels.length,
    })
  }

  function tick(now = Date.now()): void {
    clock.value = now
  }

  function agent(siteId: ID): AgentSummary {
    const a = agents.value.get(siteId) ?? EMPTY
    const stale = a.lastStatusAt === null || clock.value - a.lastStatusAt > STATUS_STALE_MS
    return { ...a, stale }
  }

  function reset(): void {
    agents.value = new Map()
    statuses.value = new Map()
    sites.value = []
  }

  return {
    wsState,
    retryDelayMs,
    connected,
    sites,
    statuses,
    clock,
    setWsState,
    setSites,
    applyAgentOnline,
    applyStatus,
    tick,
    agent,
    reset,
  }
})
